/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ForgotPasswordPage
 *
 * React page that wires ForgotPasswordForm to the backend forgot-password API.
 * For admin application, user_type is always "admin".
 *
 * Flow:
 * - User enters identifier (username/email)
 * - We call POST /<forgot-endpoint> with { identifier, user_type: "admin" }
 * - On success, show backend message as success text in the form
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ForgotPasswordForm from "../components/ForgotPasswordForm";
import APIHelper from "../api/APIHelper";
import envConfig from "../config/envConfig";
import AlertDialog from "../components/common/AlertDialog";

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

const FORGOT_PASSWORD_ENDPOINT = "/arcee/auth/password/forgot/";

function ForgotPasswordPage() {
  const navigate = useNavigate();

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("info");

  const handleForgotPassword = async ({ identifier }, setErrors) => {
    try {
      setErrors({});
      const resp = await apiHelper.apiCall(FORGOT_PASSWORD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier,
          user_type: "admin",
        }),
      });

      const data = await resp.json().catch(() => ({}));
      const message = (data && data.message) || "Something went wrong. Please try again.";

      if (!resp.ok) {
        setErrors({ identifier: message });
        return;
      }

      setAlertTitle("Password Reset");
      setAlertMessage(message);
      setAlertType("success");
      setAlertOpen(true);
    } catch (err) {
      setErrors({
        identifier: err?.message || "Unable to send reset link. Please try again.",
      });
    }
  };

  const handleAlertOk = () => {
    setAlertOpen(false);
    navigate("/sign-in");
  };

  return (
    <>
      <ForgotPasswordForm onSubmit={handleForgotPassword} />

      <AlertDialog
        open={alertOpen}
        title={alertTitle}
        message={alertMessage}
        type={alertType}
        okLabel="OK"
        showCancel={false}
        onOk={handleAlertOk}
        onCancel={() => setAlertOpen(false)}
        mini_mode={false}
      />
    </>
  );
}

export default ForgotPasswordPage;
